import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FicStatusContext = createContext(null);

/**
 * Global source of truth for the Fatture in Cloud connection status.
 * Scoped to the currently active company_profile (multi-tenant FIC).
 * Refreshes automatically when the CompanySwitcher triggers a company switch.
 */
export function FicStatusProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setStatus(null);
      return null;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/fic/status");
      setStatus(data);
      return data;
    } catch {
      setStatus({ configured: false, connected: false });
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Listen for company switch events emitted by CompanySwitcher; refresh
  // the FIC status because the connection is scoped per-company.
  useEffect(() => {
    const onSwitch = () => { refresh(); };
    window.addEventListener("company:switched", onSwitch);
    return () => window.removeEventListener("company:switched", onSwitch);
  }, [refresh]);

  const value = {
    status,
    loading,
    refresh,
    connected: !!status?.connected,
    configured: !!status?.configured,
    ready: !!(status?.connected && status?.selected_company_id),
    selectedCompanyId: status?.selected_company_id || null,
    selectedCompanyName: status?.selected_company_name || null,
  };

  return <FicStatusContext.Provider value={value}>{children}</FicStatusContext.Provider>;
}

export function useFicStatus() {
  const ctx = useContext(FicStatusContext);
  if (!ctx) {
    // Fallback (safe defaults) when used outside provider — avoids hard crashes
    return {
      status: null, loading: false, refresh: async () => null,
      connected: false, configured: false, ready: false,
      selectedCompanyId: null, selectedCompanyName: null,
    };
  }
  return ctx;
}
